import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  EyeOff,
  Eye,
  AlertTriangle,
  ExternalLink,
  User,
  Clock,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import {
  fetchBlogAdminArticles,
  approveBlogArticle,
  rejectBlogArticle,
  publishBlogArticle,
  unpublishBlogArticle,
  deleteBlogArticle,
  fetchBlogStats,
} from "../../lib/adminApi";

const STATUS_FILTERS = [
  { id: "all", label: "Tous", color: "slate" },
  { id: "pending", label: "En attente", color: "amber" },
  { id: "published", label: "Publiés", color: "emerald" },
  { id: "draft", label: "Brouillons", color: "slate" },
  { id: "rejected", label: "Rejetés", color: "red" },
];

const STATUS_META = {
  draft: { label: "Brouillon", classes: "border-slate-700 bg-slate-800/60 text-slate-300" },
  pending: { label: "En attente", classes: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  published: { label: "Publié", classes: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  rejected: { label: "Rejeté", classes: "border-red-500/40 bg-red-500/10 text-red-300" },
};

export default function AdminBlogPage() {
  const [status, setStatus] = useState("all");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = () => {
    setLoading(true);
    fetchBlogAdminArticles(status === "all" ? null : status)
      .then((d) => setArticles(d.articles || []))
      .catch(() => toast.error("Impossible de charger les articles"))
      .finally(() => setLoading(false));
    fetchBlogStats()
      .then((d) => setStats(d.counts || null))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, [status]);

  const handleApprove = async (id) => {
    try {
      await approveBlogArticle(id);
      toast.success("Article approuvé et publié");
      load();
    } catch {
      toast.error("Approbation impossible");
    }
  };
  const handlePublish = async (id) => {
    try {
      await publishBlogArticle(id);
      toast.success("Article publié");
      load();
    } catch {
      toast.error("Publication impossible");
    }
  };
  const handleUnpublish = async (id) => {
    try {
      await unpublishBlogArticle(id);
      toast.success("Article dépublié (passé en brouillon)");
      load();
    } catch {
      toast.error("Dépublication impossible");
    }
  };
  const handleReject = async () => {
    try {
      await rejectBlogArticle(rejectModal.id, rejectReason);
      toast.success("Article rejeté");
      setRejectModal(null);
      setRejectReason("");
      load();
    } catch {
      toast.error("Rejet impossible");
    }
  };
  const handleDelete = async () => {
    try {
      await deleteBlogArticle(confirmDelete.id);
      toast.success("Article supprimé définitivement");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div data-testid="admin-blog-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">
            Modération & publication
          </span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-50">
            Blog Autopièce Malin
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Articles d'Olivier et contributions visiteurs. Suppression définitive possible à tout moment.
          </p>
        </div>
        <Link to="/admin/blog/nouveau" data-testid="admin-blog-new-link">
          <Button className="gap-2 bg-cyan-500 text-slate-950 hover:bg-cyan-400">
            <Plus className="h-4 w-4" /> Nouvel article
          </Button>
        </Link>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`admin-blog-stat-${f.id}`}
              onClick={() => setStatus(f.id)}
              className={`surface-card rounded-xl p-3 text-left transition hover:-translate-y-0.5 ${
                status === f.id ? "ring-2 ring-cyan-500/50" : ""
              }`}
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {f.label}
              </div>
              <div className="mt-1 font-heading text-2xl font-extrabold text-slate-50">
                {stats[f.id] ?? 0}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="surface-card overflow-hidden rounded-2xl">
        {loading ? (
          <div className="p-10">
            <div className="h-6 w-40 rounded shimmer" />
            <div className="mt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 rounded shimmer" />
              ))}
            </div>
          </div>
        ) : articles.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            Aucun article {status !== "all" && `avec le statut "${status}"`} pour l'instant.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 bg-slate-900/60 text-left">
                <tr>
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">Article</th>
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">Auteur</th>
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">Statut</th>
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">Créé</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {articles.map((a) => {
                  const meta = STATUS_META[a.status] || STATUS_META.draft;
                  return (
                    <tr
                      key={a.id}
                      data-testid={`admin-blog-row-${a.id}`}
                      className="hover:bg-slate-900/50"
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-100">{a.title}</div>
                        <div className="mt-0.5 text-xs text-slate-500 line-clamp-1">
                          {a.excerpt}
                        </div>
                        <div className="mt-1 flex gap-1">
                          <Badge
                            variant="outline"
                            className="border-cyan-500/30 bg-cyan-500/10 text-[9px] text-cyan-300"
                          >
                            {a.category}
                          </Badge>
                          {a.author_type === "visitor" && (
                            <Badge
                              variant="outline"
                              className="border-amber-500/40 bg-amber-500/10 text-[9px] text-amber-300"
                            >
                              Visiteur
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-1.5 text-xs text-slate-300">
                          <User className="h-3 w-3 text-slate-500" />
                          {a.author_name}
                        </div>
                        {a.author_email && (
                          <div className="text-[11px] text-slate-500">{a.author_email}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Badge
                          variant="outline"
                          data-testid={`admin-blog-status-${a.id}`}
                          className={`text-[10px] uppercase ${meta.classes}`}
                        >
                          {meta.label}
                        </Badge>
                        {a.rejected_reason && (
                          <div className="mt-1 max-w-xs text-[10px] text-red-300">
                            {a.rejected_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-xs text-slate-400">
                        <div className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(a.created_at).toLocaleDateString("fr-FR")}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          {a.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                data-testid={`admin-blog-approve-${a.id}`}
                                onClick={() => handleApprove(a.id)}
                                className="h-7 gap-1 bg-emerald-500 px-2 text-xs text-slate-950 hover:bg-emerald-400"
                              >
                                <CheckCircle2 className="h-3 w-3" /> Approuver
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`admin-blog-reject-${a.id}`}
                                onClick={() => setRejectModal(a)}
                                className="h-7 gap-1 border-red-500/40 bg-red-500/10 px-2 text-xs text-red-300 hover:bg-red-500/20"
                              >
                                <XCircle className="h-3 w-3" /> Rejeter
                              </Button>
                            </>
                          )}
                          {a.status === "draft" && (
                            <Button
                              size="sm"
                              data-testid={`admin-blog-publish-${a.id}`}
                              onClick={() => handlePublish(a.id)}
                              className="h-7 gap-1 bg-cyan-500 px-2 text-xs text-slate-950 hover:bg-cyan-400"
                            >
                              <Eye className="h-3 w-3" /> Publier
                            </Button>
                          )}
                          {a.status === "published" && (
                            <>
                              <a
                                href={`/blog/${a.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-700 bg-slate-900/60 px-2 text-xs text-slate-300 hover:text-cyan-300"
                                data-testid={`admin-blog-view-${a.id}`}
                              >
                                <ExternalLink className="h-3 w-3" /> Voir
                              </a>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`admin-blog-unpublish-${a.id}`}
                                onClick={() => handleUnpublish(a.id)}
                                className="h-7 gap-1 border-slate-700 bg-slate-900/60 px-2 text-xs"
                              >
                                <EyeOff className="h-3 w-3" /> Dépublier
                              </Button>
                            </>
                          )}
                          <Link
                            to={`/admin/blog/edit/${a.id}`}
                            data-testid={`admin-blog-edit-${a.id}`}
                            className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-700 bg-slate-900/60 px-2 text-xs text-slate-300 hover:text-cyan-300"
                          >
                            <Pencil className="h-3 w-3" /> Modifier
                          </Link>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`admin-blog-delete-${a.id}`}
                            onClick={() => setConfirmDelete(a)}
                            className="h-7 gap-1 border-red-500/40 bg-red-500/10 px-2 text-xs text-red-300 hover:bg-red-500/20"
                          >
                            <Trash2 className="h-3 w-3" /> Supprimer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation suppression définitive */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="confirm-delete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300">
              <AlertTriangle className="h-5 w-5" />
              Suppression définitive
            </DialogTitle>
            <DialogDescription>
              Tu es sur le point de supprimer <strong>« {confirmDelete?.title} »</strong>.
              Cette action est <strong>irréversible</strong>, même pour un article publié
              actuellement visible en ligne.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Annuler
            </Button>
            <Button
              data-testid="confirm-delete-button"
              onClick={handleDelete}
              className="bg-red-500 text-slate-50 hover:bg-red-400"
            >
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale rejet */}
      <Dialog open={!!rejectModal} onOpenChange={(o) => !o && setRejectModal(null)}>
        <DialogContent data-testid="reject-modal">
          <DialogHeader>
            <DialogTitle>Rejeter la contribution</DialogTitle>
            <DialogDescription>
              Motif de rejet (facultatif, visible dans l'interface admin uniquement).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="reject-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ex. contenu hors sujet, pub déguisée, langue inappropriée…"
            rows={4}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectModal(null);
                setRejectReason("");
              }}
            >
              Annuler
            </Button>
            <Button
              data-testid="reject-confirm-button"
              onClick={handleReject}
              className="bg-red-500 text-slate-50 hover:bg-red-400"
            >
              Rejeter l'article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
